import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LocaleSwitcher } from "@/components/layout/locale-switcher";
import { useAuth } from "@/auth/auth-provider";
import { authErrorMessage } from "@/auth/errors";

const loginSchema = z.object({
  email: z.string().min(1, "auth.signIn.errors.emailRequired").email("auth.signIn.errors.emailInvalid"),
  password: z.string().min(1, "auth.signIn.errors.passwordRequired"),
});
type LoginValues = z.infer<typeof loginSchema>;

const newPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(12, "auth.newPassword.errors.minLength")
      .regex(/[A-Z]/, "auth.newPassword.errors.uppercase")
      .regex(/[a-z]/, "auth.newPassword.errors.lowercase")
      .regex(/[0-9]/, "auth.newPassword.errors.number")
      .regex(/[^A-Za-z0-9]/, "auth.newPassword.errors.symbol"),
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: "auth.newPassword.errors.mismatch",
    path: ["confirmPassword"],
  });
type NewPasswordValues = z.infer<typeof newPasswordSchema>;

const searchSchema = z.object({
  redirect: z.string().optional(),
});

export const Route = createFileRoute("/login")({
  validateSearch: searchSchema,
  beforeLoad: ({ context, search }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({ to: search.redirect ?? "/dashboard" });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const [stage, setStage] = useState<"signIn" | "newPassword">("signIn");
  const { t } = useTranslation();
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-10 bg-gradient-to-br from-[#2cbbce] to-[#80d12a] text-white">
        <img src="/logo.svg" alt="MStar" className="h-10 w-auto" />
        <div className="space-y-3">
          <h2 className="text-3xl font-semibold leading-tight">
            {t("auth.signIn.heroTitle")}
          </h2>
          <p className="text-white/85 max-w-md">
            {t("auth.signIn.heroDescription")}
          </p>
        </div>
        <p className="text-white/70 text-xs">{t("auth.signIn.footer")}</p>
      </div>
      <div className="relative flex items-center justify-center p-6">
        <div className="absolute top-4 right-4">
          <LocaleSwitcher align="end" />
        </div>
        {stage === "signIn" ? (
          <SignInForm onForceChangePassword={() => setStage("newPassword")} />
        ) : (
          <NewPasswordForm onCancel={() => setStage("signIn")} />
        )}
      </div>
    </div>
  );
}

function SignInForm({
  onForceChangePassword,
}: {
  onForceChangePassword: () => void;
}) {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { t } = useTranslation();
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      const result = await signIn(values.email, values.password);
      if (result.status === "SIGNED_IN") {
        navigate({ to: search.redirect ?? "/dashboard" });
      } else if (result.status === "NEW_PASSWORD_REQUIRED") {
        onForceChangePassword();
      } else if (result.status === "RESET_REQUIRED") {
        navigate({
          to: "/forgot-password",
          search: { email: values.email },
        });
      }
    } catch (err) {
      setServerError(authErrorMessage(err));
    }
  });

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="text-xl">{t("auth.signIn.title")}</CardTitle>
        <CardDescription>{t("auth.signIn.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="email">{t("auth.signIn.email")}</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              {...form.register("email")}
              aria-invalid={!!form.formState.errors.email}
            />
            {form.formState.errors.email ? (
              <p className="text-xs text-destructive">
                {t(form.formState.errors.email.message ?? "")}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">{t("auth.signIn.password")}</Label>
              <Link
                to="/forgot-password"
                className="text-xs text-primary hover:underline"
              >
                {t("auth.signIn.forgotLink")}
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              {...form.register("password")}
              aria-invalid={!!form.formState.errors.password}
            />
            {form.formState.errors.password ? (
              <p className="text-xs text-destructive">
                {t(form.formState.errors.password.message ?? "")}
              </p>
            ) : null}
          </div>

          {serverError ? (
            <div
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {serverError}
            </div>
          ) : null}

          <Button
            type="submit"
            className="w-full"
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t("auth.signIn.submitting")}
              </>
            ) : (
              t("auth.signIn.submit")
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function NewPasswordForm({ onCancel }: { onCancel: () => void }) {
  const { completeNewPassword } = useAuth();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { t } = useTranslation();
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<NewPasswordValues>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: { newPassword: "", confirmPassword: "" },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      await completeNewPassword(values.newPassword);
      navigate({ to: search.redirect ?? "/dashboard" });
    } catch (err) {
      setServerError(authErrorMessage(err));
    }
  });

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="text-xl">{t("auth.newPassword.title")}</CardTitle>
        <CardDescription>{t("auth.newPassword.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="newPassword">
              {t("auth.newPassword.newPassword")}
            </Label>
            <Input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              autoFocus
              {...form.register("newPassword")}
              aria-invalid={!!form.formState.errors.newPassword}
            />
            {form.formState.errors.newPassword ? (
              <p className="text-xs text-destructive">
                {t(form.formState.errors.newPassword.message ?? "")}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t("auth.newPassword.passwordHint")}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">
              {t("auth.newPassword.confirmPassword")}
            </Label>
            <Input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              {...form.register("confirmPassword")}
              aria-invalid={!!form.formState.errors.confirmPassword}
            />
            {form.formState.errors.confirmPassword ? (
              <p className="text-xs text-destructive">
                {t(form.formState.errors.confirmPassword.message ?? "")}
              </p>
            ) : null}
          </div>

          {serverError ? (
            <div
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {serverError}
            </div>
          ) : null}

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onCancel}
              disabled={form.formState.isSubmitting}
            >
              {t("common.actions.cancel")}
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {t("auth.newPassword.submitting")}
                </>
              ) : (
                t("auth.newPassword.submit")
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
